import * as React from 'react'
import { memo, useMemo, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import useMediaQuery from '@mui/material/useMediaQuery'
// The commented-out API Manager block needs Button re-imported if re-enabled.
import ButtonBase from '@mui/material/ButtonBase'
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { ComponentPropType } from 'ra-core'
import { UserMenu as DefaultUserMenu } from 'ra-ui-materialui'
import { HideOnScroll } from 'ra-ui-materialui'
import { usePermissions } from '@hooks/useHasPermission'
import MyUserMenu from './CustomUserMenu'
import { AccountSearch } from '@/dashboard/AdminSearch'
import { PropertySearch } from '@/dashboard/PropertySearchBar'

import Logo from './Logo'
import LogoutButton from '@component/Auth/LogoutButton'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { Loading } from 'ra-ui-materialui'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { IEntity } from '../types'
import { setManagementTag } from '@reducer/profile'
import DashboardThemeToggle from '@/dashboard/DashboardThemeToggle'
import {
  DashboardTheme,
  THEME_EVENT,
  getDashboardTheme,
  setDashboardTheme as persistDashboardTheme,
} from '@/dashboard/dashboardTheme'

const AccountSearchStyle = { flex: '1 1 auto', maxWidth: 300, minWidth: 80, marginLeft: 20 }

const useStyles = makeStyles(
  (theme: Theme) => ({
    // The 2026 portfolio dashboard redesign's top bar: indigo to lavender, left to
    // right (the mockup's .top-nav). Painted on the AppBar root as well as the
    // toolbar so no sliver of the theme's blue shows around the toolbar's edge.
    appBar: {
      backgroundColor: '#4A3D9E',
      backgroundImage: 'linear-gradient(90deg, #4A3D9E, #8B7FE0)',
    },
    toolbar: {
      height: 'var(--app-bar-h, 72px)',
      paddingRight: 24,
      backgroundColor: '#4A3D9E',
      backgroundImage: 'linear-gradient(90deg, #4A3D9E, #8B7FE0)',
    },
    menuButton: {
      [theme.breakpoints.up('sm')]: {
        width: 50,
      },
      display: 'flex',
      justifyContent: 'flex-start',
      marginLeft: '0.2em',
      marginRight: '0.2em',
    },
    menuButtonIconClosed: {},
    menuButtonIconOpen: {},
    title: {
      flex: 1,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    spacer: {
      flex: 1,
    },
    logoBox: {
      display: 'flex',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      padding: '6px 16px',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    logoButton: {
      borderRadius: 12,
      padding: 4,
      flexShrink: 0,
    },
    logo: {
      //    marginLeft: -100
    },
    toggleBox: {
      marginLeft: 12,
      marginRight: 12,
      flexShrink: 0,
    },
    apiButton: {
      marginLeft: 12,
      marginRight: 12,
      borderRadius: 999,
      textTransform: 'none',
      fontWeight: 700,
      padding: '6px 16px',
      whiteSpace: 'nowrap',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.65)',
      backgroundColor: 'rgba(255,255,255,0.12)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.22)',
      },
    },
    managementTag: {
      height: 48,
      backgroundColor: 'white',
      borderRadius: 4,
      '&[class*=MuiOutlinedInput-root]': {
        padding: '0 9px',
      },
    },
  }),
  { name: 'RaAppBar' }
)

const CustomUserMenu = (props: any) => {
  return (
    <MyUserMenu {...props}>
      <LogoutButton />
    </MyUserMenu>
  )
}

const AppBarExtend = (props: AppBarProps): JSX.Element => {
  const {
    className,
    color = 'secondary',
    logout,
    open: _open,
    title: _title,
    userMenu: _userMenu,
    container: _container,
    children: _children,
    alwaysOn: _alwaysOn,
    ...rest
  } = props as any
  void _open
  void _title
  void _userMenu
  void _container
  void _children
  void _alwaysOn
  const classes = useStyles(props)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, selectedPropertyId } = useSelector((state: any) => state.profile)
  const [managementTagOptions, setManagementTagOptions] = useState<{ TagId: string; Name: string }[]>([])
  const [dashboardTheme, setDashboardTheme] = useState(getDashboardTheme)
  const [apiManagerOpen, setApiManagerOpen] = useState(false)
  const { selectedManagementTag } = useSelector((state) => state.profile)
  const permissions = usePermissions()
  const { GlobalManageCustomers, PortfolioTeam } = permissions || {}
  const dispatch = useDispatch()
  const isXSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down('xs'))
  const disabled = location.pathname.includes('/surveyWelcome')
  const { hiddenBarProperty, showLoginGroup } = useMemo(() => location.state || {}, [location.state])
  const hiddenProperty = disabled || hiddenBarProperty
  const [tagsResolved, setTagsResolved] = useState(false)
  const handleEntitiesLoaded = useCallback((entities: IEntity[]) => {
    const tagsById = new Map<string, string>()
    entities.forEach((entity) => {
      const { TagId, Name } = entity.ManagementTag || {}
      if (TagId && Name) tagsById.set(TagId, Name)
    })
    setManagementTagOptions(
      Array.from(tagsById, ([TagId, Name]) => ({ TagId, Name })).sort((a, b) => a.Name.localeCompare(b.Name))
    )
    setTagsResolved(true)
  }, [])
  // The selected tag can go away under the filter — its last building untagged, or the tag
  // dropped from the building being viewed. The dropdown itself then shows a placeholder,
  // but the selection survives in redux and PropertySearchBar keeps filtering the building
  // list down to nothing. Drop it once a real building list says the tag is gone; waiting
  // for tagsResolved keeps a persisted selection alive across the first empty render.
  useEffect(() => {
    if (!tagsResolved || !selectedManagementTag?.TagId) return
    if (managementTagOptions.some((tag) => tag.TagId === selectedManagementTag.TagId)) return
    dispatch(setManagementTag({ managementTag: undefined }))
  }, [tagsResolved, managementTagOptions, selectedManagementTag, dispatch])
  useEffect(() => {
    const syncTheme = () => setDashboardTheme(getDashboardTheme())
    window.addEventListener(THEME_EVENT, syncTheme)
    window.addEventListener('storage', syncTheme)
    return () => {
      window.removeEventListener(THEME_EVENT, syncTheme)
      window.removeEventListener('storage', syncTheme)
    }
  }, [])
  const isNewTheme = dashboardTheme === 'new'
  const toggleTheme = () => {
    const nextTheme: DashboardTheme = isNewTheme ? 'legacy' : 'new'
    setDashboardTheme(nextTheme)
    persistDashboardTheme(nextTheme)
    if (nextTheme === 'new') {
      navigate('/')
    }
  }
  const handleLogoClick = () => {
    navigate('/')
  }
  if (!user) {
    return <Loading />
  }
  return (
    <AppBar className={`${classes.appBar} ${className || ''}`} color={color} {...rest}>
      <Toolbar disableGutters variant={isXSmall ? 'regular' : 'dense'} className={classes.toolbar}>
        <Box className={classes.logoBox}>
          <ButtonBase
            className={classes.logoButton}
            onClick={handleLogoClick}
            disableRipple
            aria-label="Go to dashboard"
          >
            <Logo />
          </ButtonBase>
          <AccountSearch style={AccountSearchStyle} isBarSearch onlyShowDefault={disabled || (showLoginGroup as boolean)} />
          {!hiddenProperty && managementTagOptions.length > 0 && (
            <Autocomplete
              style={AccountSearchStyle}
              classes={{ inputRoot: classes.managementTag }}
              options={managementTagOptions}
              value={managementTagOptions.find((tag) => tag.TagId === selectedManagementTag?.TagId) ?? null}
              onChange={(_, tag) => {
                dispatch(setManagementTag({ managementTag: tag || undefined }))
              }}
              getOptionLabel={(tag) => tag.Name || ''}
              isOptionEqualToValue={(option, value) => option.TagId === value?.TagId}
              renderInput={(params) => <TextField {...params} placeholder="Property Management" variant="outlined" />}
              popupIcon={<ExpandMore />}
            />
          )}
          {!hiddenProperty && (
            <PropertySearch
              style={AccountSearchStyle}
              isBarSearch
              filterByManagementTag
              onEntitiesLoaded={handleEntitiesLoaded}
              allowedBuildingIds={
                PortfolioTeam && !GlobalManageCustomers ? user?.UserContext?.AccessBuildingIds : undefined
              }
            />
          )}
        </Box>
        {/* API Manager — hidden until functionality is implemented
        <Button className={classes.apiButton} startIcon={<SettingsIcon />} onClick={() => setApiManagerOpen(true)}>
          API Manager
        </Button>
        */}
        <Box className={classes.toggleBox}>
          <DashboardThemeToggle
            checked={isNewTheme}
            onChange={toggleTheme}
            tone="light"
            variant={isNewTheme ? 'new' : 'legacy'}
          />
        </Box>
        <CustomUserMenu logout={logout} />
      </Toolbar>
      {/* <ApiManagerDialog open={apiManagerOpen} onClose={() => setApiManagerOpen(false)} /> */}
    </AppBar>
  )
}

AppBarExtend.propTypes = {
  children: PropTypes.node,
  // @ts-ignore
  classes: PropTypes.object,
  className: PropTypes.string,
  color: PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary', 'transparent']),
  container: ComponentPropType,
  logout: PropTypes.element,
  // @deprecated
  open: PropTypes.bool,
  userMenu: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
}

AppBarExtend.defaultProps = {
  userMenu: <DefaultUserMenu />,
  container: HideOnScroll,
}

export interface AppBarProps extends Omit<MuiAppBarProps, 'title' | 'classes'> {
  container?: React.ElementType<any>
  logout?: React.ReactNode
  // @deprecated
  open?: boolean
  title?: string | JSX.Element
  userMenu?: JSX.Element | boolean
}

export default memo(AppBarExtend)
